import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Output, inject } from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  ErrorComponent,
  LicenseEntitlement,
  LicenseType,
  Organization,
  OrganizationService,
} from '@nimmsta/ng-commons-lib';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { LaddaModule } from 'angular2-ladda';
import { Select } from 'primeng/select';
import {
  hasDuplicateLicenseTypes,
  isNonNegativeInteger,
} from './license-entitlement-form.utils';

type LicenseEntitlementFormGroup = FormGroup<{
  type: FormControl<LicenseType | null>;
  purchasedCount: FormControl<number | null>;
}>;

interface LicenseTypeOption {
  label: string;
  value: LicenseType;
}

@Component({
  selector: 'app-organization-license-entitlements-form',
  templateUrl: './organization-license-entitlements-form.component.html',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    Select,
    ErrorComponent,
    LaddaModule,
  ],
})
export class OrganizationLicenseEntitlementsFormComponent {
  private organizationService = inject(OrganizationService);
  private translate = inject(TranslateService);

  @Output() closeEvent = new EventEmitter<void>();
  @Output() loadingEvent = new EventEmitter<boolean>();

  organization?: Organization;
  error?: HttpErrorResponse;
  loading = false;

  readonly form = new FormGroup({
    entitlements: new FormArray<LicenseEntitlementFormGroup>([]),
  });

  readonly licenseTypes = Object.values(LicenseType);

  get entitlements(): FormArray<LicenseEntitlementFormGroup> {
    return this.form.controls.entitlements;
  }

  load(organization: Organization): void {
    this.organization = organization;
    this.error = undefined;
    this.setLoading(true);
    this.entitlements.clear();

    this.organizationService.getLicenseEntitlements(organization.id).subscribe({
      next: (response) => {
        response.entitlements.forEach((entitlement) =>
          this.addEntitlement(entitlement),
        );
        this.form.markAsPristine();
        this.setLoading(false);
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
        this.setLoading(false);
      },
    });
  }

  addEntitlement(entitlement?: LicenseEntitlement): void {
    if (!entitlement && this.entitlements.length >= this.licenseTypes.length) {
      return;
    }

    this.entitlements.push(
      new FormGroup({
        type: new FormControl<LicenseType | null>(
          entitlement?.type ?? null,
          Validators.required,
        ),
        purchasedCount: new FormControl<number | null>(
          entitlement?.purchasedCount ?? null,
          [
            Validators.required,
            (control) =>
              isNonNegativeInteger(control.value)
                ? null
                : { nonNegativeInteger: true },
          ],
        ),
      }),
    );
  }

  removeEntitlement(index: number): void {
    this.entitlements.removeAt(index);
    this.form.markAsDirty();
  }

  availableLicenseTypes(index: number): LicenseTypeOption[] {
    const selectedInOtherRows = new Set(
      this.entitlements.controls
        .filter((_, rowIndex) => rowIndex !== index)
        .map((control) => control.controls.type.value)
        .filter((value): value is LicenseType => value !== null),
    );

    return this.licenseTypes
      .filter((type) => !selectedInOtherRows.has(type))
      .map((type) => ({
        value: type,
        label: this.translate.instant(
          `organizations.licenseEntitlements.types.${type}`,
        ),
      }));
  }

  hasDuplicateTypes(): boolean {
    return hasDuplicateLicenseTypes(
      this.entitlements.controls.map((control) => control.controls.type.value),
    );
  }

  save(): void {
    if (this.form.invalid || this.hasDuplicateTypes() || !this.organization) {
      this.form.markAllAsTouched();
      return;
    }

    const entitlements = this.entitlements.controls.map((control) => ({
      type: control.controls.type.value!,
      purchasedCount: control.controls.purchasedCount.value!,
    }));

    this.error = undefined;
    this.setLoading(true);
    this.organizationService
      .updateLicenseEntitlements(this.organization.id, { entitlements })
      .subscribe({
        next: () => {
          this.form.markAsPristine();
          this.setLoading(false);
          this.closeEvent.emit();
        },
        error: (error: HttpErrorResponse) => {
          this.error = error;
          this.setLoading(false);
        },
      });
  }

  private setLoading(loading: boolean): void {
    this.loading = loading;
    this.loadingEvent.emit(loading);
  }
}
